import React, { useEffect, useRef, useState, useCallback } from "react"
import { Howl } from "howler"
import celebrationSound from "../../assets/audio/claps.mp3"

const COLORS = ["#ff6b6b", "#ffd93d", "#6bcb77", "#4d96ff", "#c77dff", "#ff9f43", "#ff6bcd"]
const rand = (a, b) => a + Math.random() * (b - a)

// Sound envelope tuning
const PEAK_VOLUME = 0.85
const FADE_IN_MS = 600
const FADE_OUT_MS = 900

export default function CelebrationOverlay({ triggerCelebration, onComplete }) {
  const [active, setActive] = useState(false)
  const [soundReady, setSoundReady] = useState(false)
  const canvasRef = useRef(null)
  const animRef = useRef(null)
  const soundRef = useRef(null)
  const fadeOutTimerRef = useRef(null)
  const firedRef = useRef(false)

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
  }, [])

  const launchBurst = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    resizeCanvas()
    const ctx = canvas.getContext("2d")
    if (animRef.current) cancelAnimationFrame(animRef.current)

    // Increased particle count for a bigger confetti burst
    const particles = Array.from({ length: 450 }, () => ({
      x: rand(0, canvas.width),
      y: rand(-canvas.height * 0.2, 0),
      vx: rand(-2, 2),
      vy: rand(2, 6),
      gravity: 0.15,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      w: rand(6, 12),
      h: rand(4, 8),
      angle: rand(0, Math.PI * 2),
      spin: rand(-0.15, 0.15),
      isCircle: Math.random() > 0.5,
    }))

    const start = performance.now()
    const DURATION = 8000

    const draw = (now) => {
      const elapsed = now - start
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      particles.forEach(p => {
        p.vy += p.gravity * 0.15
        p.x += p.vx
        p.y += p.vy
        p.angle += p.spin
        const life = Math.max(0, 1 - elapsed / DURATION)
        ctx.save()
        ctx.globalAlpha = life
        ctx.translate(p.x, p.y)
        ctx.rotate(p.angle)
        ctx.fillStyle = p.color
        if (p.isCircle) {
          ctx.beginPath()
          ctx.ellipse(0, 0, p.w / 2, p.h / 2, 0, 0, Math.PI * 2)
          ctx.fill()
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
        }
        ctx.restore()
      })

      if (elapsed < DURATION) {
        animRef.current = requestAnimationFrame(draw)
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setActive(false)
        setSoundReady(false)
        if (onComplete) onComplete()
      }
    }
    animRef.current = requestAnimationFrame(draw)
  }, [resizeCanvas, onComplete])

  useEffect(() => {
    if (triggerCelebration && !firedRef.current) {
      firedRef.current = true
      setActive(true)
      setSoundReady(true)
    }
    if (!triggerCelebration) {
      firedRef.current = false
    }
  }, [triggerCelebration])

  useEffect(() => {
    if (!active) return
    launchBurst()
    window.addEventListener("resize", resizeCanvas)
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current)
      window.removeEventListener("resize", resizeCanvas)
    }
  }, [active, launchBurst, resizeCanvas])

  // Applies the fade-in / hold / fade-out envelope once the sound is actually playing.
   // Applies the fade-in / hold / fade-out envelope once the sound is actually playing.
  const scheduleEnvelope = useCallback((sound) => {
    const runEnvelope = () => {
      // Guard: if duration still isn't available, retry shortly instead of using a bad value.
      const durationMs = sound.duration() * 1000
      if (!durationMs) {
        setTimeout(runEnvelope, 50)
        return
      }

      sound.volume(0)
      sound.fade(0, PEAK_VOLUME, FADE_IN_MS)

      const fadeOutDelay = Math.max(FADE_IN_MS, durationMs - FADE_OUT_MS)

      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current)
      fadeOutTimerRef.current = setTimeout(() => {
        if (soundRef.current === sound) {
          sound.fade(sound.volume(), 0, FADE_OUT_MS)
        }
      }, fadeOutDelay)
    }

    // If Howler already has the duration cached (e.g. reused/preloaded sound), use it now.
    if (sound.state() === "loaded") {
      runEnvelope()
    } else {
      sound.once("load", runEnvelope)
    }
  }, [])

  useEffect(() => {
    if (!active) return
    const sound = new Howl({ src: [celebrationSound], volume: 0 })
    soundRef.current = sound

    sound.play()

    sound.once('playerror', () => {
      setSoundReady(true) // autoplay blocked, show button as fallback
    })
    sound.once('play', () => {
      setSoundReady(false) // autoplay worked, hide button
      scheduleEnvelope(sound)
    })

    return () => {
      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current)
      sound.stop()
      sound.unload()
      soundRef.current = null
    }
  }, [active, scheduleEnvelope])

  // This is the ONLY place .play() is called manually — directly inside a real click.
  const handlePlaySound = () => {
    if (soundRef.current) {
      soundRef.current.once('play', () => scheduleEnvelope(soundRef.current))
      soundRef.current.play()
    }
    setSoundReady(false)
  }

  if (!active) return null

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed", top: 0, left: 0,
          width: "100vw", height: "100vh",
          pointerEvents: "none", zIndex: 9999,
        }}
      />
      {soundReady && (
        <button
          onClick={handlePlaySound}
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 10000,
            padding: "10px 16px", borderRadius: 24, border: "none",
            background: "#333", color: "#fff", cursor: "pointer",
          }}
        >
          🔊 Play celebration sound
        </button>
      )}
    </>
  )
}